import {
  Building,
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  DollarSign,
  Gift,
  HelpCircle,
  Image as ImageIcon,
  Inbox,
  LayoutDashboard,
  Mail,
  MapPin,
  Send,
  Sliders,
  Star,
  Users,
} from 'lucide-react';
import type {
  AddOrganizationFormValues,
  BrandingPreviewNavItem,
  BusinessUnitItem,
  DepartmentItem,
  DesignationItem,
  EntityModalType,
  GradeItem,
  LocationItem,
  PreviewType,
  TabType,
} from '@/types/ConfigOrg';

export const BRAND_COLOR_PRESETS = [
  { name: 'QHRMS Emerald', value: '#219268' },
  { name: 'Classic Navy', value: '#0C1B33' },
  { name: 'Corporate Teal', value: '#0891B2' },
  { name: 'Royal Purple', value: '#7C3AED' },
  { name: 'Warm Amber', value: '#D97706' },
  { name: 'Crimson Red', value: '#DC2626' },
];

export const INITIAL_GENERAL_CONFIG = {
  subdomain: 'qubiqon.qhrms.com',
  organizationName: 'Qubiqon Technologies Private Limited',
  shortName: 'QUBIQON',
  financialYear: '4',
  timeZone: 'Asia/Kolkata',
  dateFormat: 'DD/MM/YYYY',
  currency: 'INR',
  companyLegalName: 'Qubiqon Technologies Private Limited',
  tradeName: 'Qubiqon',
  registrationNumber: 'U72200KA2021PTC147852',
  streetAddress: '12, Bannerghatta Main Rd, JP Nagar 3rd Phase',
  city: 'Bengaluru',
  state: 'Karnataka',
  pin: '560076',
  country: 'India',
  panNumber: 'AAACQ1234A',
  gstNumber: '29AAACQ1234A1Z5',
  tanNumber: 'BLRO12345A',
  natureOfBusiness: 'Information Technology & Software Services',
  industryType: 'IT / Software',
  incorporationDate: '2021-04-12',
  signatoryName: 'Saraswathi Kumar',
  signatoryDesignation: 'Managing Director',
  logoUrl: null as string | null,
};

export const FONT_COLOR_PRESETS = [
  { name: 'Charcoal', value: '#344054' },
  { name: 'Navy', value: '#0C1B33' },
  { name: 'Slate', value: '#334155' },
  { name: 'Black', value: '#0F172A' },
  { name: 'Warm Gray', value: '#44403C' },
  { name: 'Deep Teal', value: '#134E4A' },
];

export const BODY_FONT_COLOR_PRESETS = [
  { name: 'Medium Gray', value: '#667085' },
  { name: 'Slate Gray', value: '#64748B' },
  { name: 'Cool Gray', value: '#6B7280' },
  { name: 'Warm Gray', value: '#78716C' },
  { name: 'Blue Gray', value: '#4B5563' },
  { name: 'Muted Ink', value: '#52525B' },
];

export const INITIAL_BRANDING_CONFIG = {
  organizationLogoUrl: null as string | null,
  companyLogo: null as string | null,
  favicon: null as string | null,
  brandColor: '#219268',
  fontColor: '#344054',
  bodyFontColor: '#667085',
  themeMode: 'light' as const,
};

export const INITIAL_LOCATIONS: LocationItem[] = [];

export const INITIAL_EMAIL_CONFIG = {
  notificationEmail: '',
  displayName: '',
};

export const INITIAL_BUSINESS_UNITS: BusinessUnitItem[] = [];
export const INITIAL_DEPARTMENTS: DepartmentItem[] = [];
export const INITIAL_DESIGNATIONS: DesignationItem[] = [];
export const INITIAL_GRADES: GradeItem[] = [];

export const INITIAL_ADD_ORGANIZATION_FORM: AddOrganizationFormValues = {
  companyLegalName: '',
  shortName: '',
  subdomain: '',
  country: 'India',
  industryType: '',
};

export const ENTITY_MODAL_TITLES: Record<EntityModalType, string> = {
  location: 'Office Location',
  bu: 'Business Unit',
  department: 'Department',
  designation: 'Designation',
  grade: 'Employee Grade',
};

export const CONFIG_ORG_TABS: { id: TabType; label: string; icon: typeof Building }[] = [
  { id: 'general', label: 'General & Legal', icon: Building },
  { id: 'branding', label: 'Interface Branding', icon: ImageIcon },
  { id: 'locations', label: 'Office Locations', icon: MapPin },
  { id: 'structure', label: 'Business Structure', icon: Sliders },
  { id: 'email', label: 'Email Server Settings', icon: Mail },
];

export const BRANDING_PREVIEW_TABS: { id: PreviewType; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'leave', label: 'Leave' },
  { id: 'attendance', label: 'Attendance' },
];

export const BRANDING_PREVIEW_WIDTH = 1280;
export const BRANDING_PREVIEW_HEIGHT = 800;
export const INITIAL_BRANDING_PREVIEW_SCALE = 0.55;

export const BRANDING_PREVIEW_NAV_ITEMS: BrandingPreviewNavItem[] = [
  { id: 'dashboard', label: 'My Space', icon: LayoutDashboard },
  { id: 'leave', label: 'Leave', icon: Calendar },
  { id: 'attendance', label: 'Attendance', icon: Clock },
  { id: 'other', label: 'Holidays', icon: Gift },
  { id: 'other', label: 'Payroll', icon: DollarSign },
  { id: 'other', label: 'Requests', icon: Send },
  { id: 'other', label: 'Helpdesk', icon: HelpCircle },
  { id: 'other', label: 'Request Centre', icon: Inbox },
  { id: 'other', label: 'Org Hierarchy', icon: Users },
];

export const BRANDING_PREVIEW_DASHBOARD_STATS = [
  { label: 'Leave Balance', value: '12 days', sub: 'Casual Leave', color: 'text-brand-primary', bgColor: 'bg-brand-primary-soft', icon: Calendar },
  { label: 'Attendance', value: 'Present', sub: 'Today · Office', color: 'text-brand-green', bgColor: 'bg-brand-green-soft', icon: CheckCircle },
  { label: 'Pending Reviews', value: '-', sub: 'Annual Review', color: 'text-brand-amber', bgColor: 'bg-brand-amber-soft', icon: Star },
  { label: 'Open Tickets', value: '1', sub: 'Helpdesk', color: 'text-brand-purple', bgColor: 'bg-brand-purple-soft', icon: HelpCircle },
  { label: 'Pending Approvals', value: '3', sub: '2 Leave · 1 WFH', color: 'text-brand-teal', bgColor: 'bg-brand-teal-soft', icon: ClipboardList },
];

export const BRANDING_PREVIEW_DASHBOARD_QUICK_ACTIONS = [
  { label: 'Apply Leave', icon: Calendar },
  { label: 'Submit Attendance', icon: Clock },
  { label: 'View Holidays', icon: Gift },
  { label: 'Raise Request', icon: Send },
];

export const BRANDING_PREVIEW_LEAVE_BALANCES = [
  { name: 'Casual Leave', available: 12, used: 3, total: 15, color: 'text-brand-primary', bg: 'bg-brand-primary-soft' },
  { name: 'Sick Leave', available: 8, used: 2, total: 10, color: 'text-brand-green', bg: 'bg-brand-green-soft' },
  { name: 'Earned Leave', available: 18, used: 7, total: 25, color: 'text-brand-teal', bg: 'bg-brand-teal-soft' },
];

export const BRANDING_PREVIEW_LEAVE_HISTORY = [
  { type: 'Casual Leave', from: '10 Jun 2026', to: '11 Jun 2026', days: 2, status: 'Approved' },
  { type: 'Sick Leave', from: '28 May 2026', to: '28 May 2026', days: 1, status: 'Approved' },
  { type: 'Earned Leave', from: '15 Apr 2026', to: '19 Apr 2026', days: 5, status: 'Pending' },
];

export const BRANDING_PREVIEW_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const BRANDING_PREVIEW_ATTENDANCE_STATS = [
  { label: 'Present', value: 18, sub: '/ 22 days', color: 'text-brand-green' },
  { label: 'Absent', value: 1, color: 'text-brand-red' },
  { label: 'On Leave', value: 2, color: 'text-brand-primary' },
  { label: 'WFH', value: 3, color: 'text-brand-purple' },
  { label: 'Half Day', value: 0, color: 'text-brand-amber' },
  { label: 'Pending', value: 1, color: 'text-orange-500' },
];
